/**
 * voice-v8 — Projekte-View (Phase E3.3).
 *
 * List + Card + simple Detail-Sheet. Inline-Edit per prompt() (pragmatisch
 * für v1; TODO: Detail-Modal v2).
 */

import { projektMeta } from "./projekt-meta.js";
import {
  archiveProject,
  createProject,
  createProjectFile,
  deleteProject,
  getActiveProject,
  getProject,
  getProjectDocument,
  getProjectReport,
  getProjectTimeline,
  linkToProject,
  listProjectFiles,
  listProjects,
  listRelated,
  requestProjectReport,
  getCleanupPlan,
  applyCleanup,
  setActiveProject,
  type ProjectDto,
  type ProjectFileDto,
  type ProjectFileType,
  type ProjectLinkDto,
  regenerateProjectDocument,
  unarchiveProject,
  unlinkFromProject,
  updateProject,
  uploadProjectFile,
} from "./projects-api.js";
import { icon } from "./icons.js";
import { gesteuert } from "./ruckel-schalter.js";
import { renderMarkdown } from "./markdown.js";
import { splitDocChapters } from "./doc-narration.js";
import {
  formatBytes,
  openProjectFileViewer,
  typeLabel,
} from "./project-file-viewer.js";
import {
  requestGoChat,
  buildCloseButton,
  confirmSheet,
  promptSheet, buildLoadingHint, buildPageHeader } from "./sheet-ui.js";
import { pushView, closeTopmost } from "./view-stack.js";
import { vorschauOhneEcho } from "./vorschau-ohne-echo.js";

interface ProjectsViewOptions {
  /** Wird gerufen wenn der User das aktive Projekt geändert hat — App
   * kann den Status-Bar-Slot updaten. */
  onActiveChange?: (project: ProjectDto | null) => void;
  /** Player-Builder wird von app.ts injiziert (vermeidet Zirkular-Import mit
   * app.ts, wo buildNarrationPlayer lebt). Liefert das Player-Element zum
   * Einhängen über dem Überblickstext. */
  buildPlayer?: (opts: {
    endpoint: string;
    getBody: (voiceId: string) => Record<string, unknown> | null;
    sectionable?: boolean;
    /** Positions-Resume: zuletzt gehörte Sekunde pro Inhalt merken (s.
     * playback-resume). resumeVersion bindet die Position an die Dokument-
     * Version → neuer Überblick ⇒ frischer Start. */
    resumeId?: string;
    resumeVersion?: string;
  }) => {
    el: HTMLElement;
    /** Pre.983: Abschnitte (Kapitel) setzen → Vor/Zurück-Blättern im Player. */
    setSections: (items: Array<{ title: string; text: string }>) => void;
  };
}

/**
 * Pre.959: Server-Zeitstempel (UTC — SQLite „YYYY-MM-DD HH:MM:SS" oder ISO) →
 * lesbares lokales Datum + Uhrzeit. Hängt „Z" an, wenn keine Zone vorhanden ist
 * (sonst läge die Anzeige um den UTC-Offset daneben — gleiche Logik wie
 * parseServerTs in app.ts). Leerer String, wenn nicht parsebar.
 */
function formatDocDate(ts: string | null | undefined): string {
  if (!ts) return "";
  let iso = ts.includes("T") ? ts : ts.replace(" ", "T");
  if (!/[zZ]$|[+-]\d\d:?\d\d$/.test(iso)) iso += "Z";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  /*
   * ⚠️ 15.09.2026: Stand hier als „Mo., 14. September 2026 um 23:46" und
   *    brauchte auf 390 Punkten ZWEI Zeilen (gemessen: 42 Punkte hoch) fuer
   *    eine Nebenangabe. Heute und gestern brauchen gar kein Datum — wer
   *    genau hinsehen will, findet es im `title`.
   */
  const heute = new Date();
  const wien = (x: Date): string =>
    x.toLocaleDateString("de-AT", { timeZone: "Europe/Vienna" });
  const uhr = d.toLocaleTimeString("de-AT", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Vienna",
  });
  if (wien(d) === wien(heute)) return `heute, ${uhr}`;
  const gestern = new Date(heute.getTime() - 86_400_000);
  if (wien(d) === wien(gestern)) return `gestern, ${uhr}`;
  return `${d.toLocaleDateString("de-AT", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "Europe/Vienna",
  })}, ${uhr}`;
}

/** Die volle Angabe fuer den `title` — nachschlagbar, aber nicht im Weg. */
function formatDocDateLang(ts: string | null | undefined): string {
  if (!ts) return "";
  let iso = ts.includes("T") ? ts : ts.replace(" ", "T");
  if (!/[zZ]$|[+-]\d\d:?\d\d$/.test(iso)) iso += "Z";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("de-AT", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Vienna",
  });
}

const DEFAULT_COLORS = [
  "#a33a0a",
  "#7d8c54",
  "#8d6748",
  "#5e7d8c",
  "#a55a8c",
  "#c89c4f",
];

/** „1 Datei", nicht „1 Dateien". */
function zaehlwort(n: number, eins: string, viele: string): string {
  return `${n} ${n === 1 ? eins : viele}`;
}

export class ProjectsView {
  private root: HTMLElement;
  private listContainer!: HTMLElement;
  private headerEl!: HTMLElement;
  private showArchived = false;
  private projects: ProjectDto[] = [];
  private active: ProjectDto | null = null;

  constructor(
    root: HTMLElement,
    private opts: ProjectsViewOptions = {},
  ) {
    this.root = root;
    while (root.firstChild) root.removeChild(root.firstChild);
    root.classList.add("projects-view");
    this.renderShell();
    void this.refresh();
  }

  private renderShell(): void {
    // NORM 2026-07-21: einheitlicher Kopf — Pfeil zurueck zum Chat,
    // '+ Neues Projekt' als sichtbare Aktion, Archiv-Umschalter im ⋯-Menue.
    const newBtn = document.createElement("button");
    newBtn.type = "button";
    newBtn.className = "projects-btn projects-btn-primary";
    newBtn.textContent = "+ Neu";
    newBtn.addEventListener("click", () => void this.handleCreate());

    this.headerEl = buildPageHeader("Projekte", () => requestGoChat(), {
      reopenMenu: false,
      action: newBtn,
      menuItems: [
        {
          label: "Archivierte ein-/ausblenden",
          onSelect: () => {
            this.showArchived = !this.showArchived;
            void this.refresh();
          },
        },
      ],
    });
    this.root.appendChild(this.headerEl);

    this.listContainer = document.createElement("div");
    this.listContainer.className = "projects-list";
    this.root.appendChild(this.listContainer);
  }

  async refresh(): Promise<void> {
    if (this.listContainer && !this.listContainer.hasChildNodes()) {
      this.listContainer.replaceChildren(buildLoadingHint("Lade Projekte \u2026"));
    }
    try {
      [this.projects, this.active] = await Promise.all([
        listProjects({ archived: this.showArchived }),
        getActiveProject(),
      ]);
      this.renderList();
    } catch (err) {
      this.listContainer.textContent = `Laden fehlgeschlagen: ${
        err instanceof Error ? err.message : String(err)
      }`;
    }
  }

  async openProjectById(id: string): Promise<void> {
    if (this.projects.length === 0) await this.refresh();
    const p = this.projects.find((proj) => proj.id === id);
    if (p) await this.openDetail(p);
  }

  // #181: Öffnet ein Projekt-Detail anhand von ID ODER Name. Auflösung:
  // ID-Lookup → exakter case-insensitive Name → fuzzy contains. Liefert true,
  // wenn ein Projekt gefunden und geöffnet wurde, sonst false (Aufrufer bleibt
  // dann auf der reinen Übersicht). Findet bei Bedarf auch archivierte.
  async openProjectByNameOrId(nameOrId: string): Promise<boolean> {
    const want = nameOrId.trim();
    if (!want) return false;
    if (this.projects.length === 0) await this.refresh();
    let p = this.resolveProject(want);
    // Nicht gefunden? Einmal mit archivierten nachladen und erneut auflösen.
    if (!p && !this.showArchived) {
      const all = await listProjects({ archived: true }).catch(() => []);
      const wantLc = want.toLowerCase();
      p =
        all.find((proj) => proj.id === want) ??
        all.find((proj) => proj.name.toLowerCase() === wantLc) ??
        all.find((proj) => proj.name.toLowerCase().includes(wantLc));
    }
    if (!p) return false;
    await this.openDetail(p);
    return true;
  }

  // #181: ID zuerst, dann exakter Name (case-insensitive), dann fuzzy.
  private resolveProject(want: string): ProjectDto | undefined {
    const wantLc = want.toLowerCase();
    return (
      this.projects.find((p) => p.id === want) ??
      this.projects.find((p) => p.name.toLowerCase() === wantLc) ??
      this.projects.find((p) => p.name.toLowerCase().includes(wantLc))
    );
  }

  private renderList(): void {
    while (this.listContainer.firstChild) {
      this.listContainer.removeChild(this.listContainer.firstChild);
    }

    if (this.projects.length === 0) {
      const empty = document.createElement("div");
      empty.className = "projects-empty";
      empty.textContent = this.showArchived
        ? "Keine Projekte (auch keine archivierten)."
        : "Noch keine Projekte. Leg eins an mit + Neu.";
      this.listContainer.appendChild(empty);
      return;
    }

    for (const p of this.projects) {
      this.listContainer.appendChild(this.renderCard(p));
    }
  }

  private renderCard(p: ProjectDto): HTMLElement {
    const card = document.createElement("article");
    card.className = "project-card";
    if (p.archivedAt) card.classList.add("is-archived");
    if (this.active?.id === p.id) card.classList.add("is-active");

    const dot = document.createElement("span");
    dot.className = "project-color-dot";
    if (p.color) dot.style.background = p.color;
    card.appendChild(dot);

    const main = document.createElement("button");
    main.type = "button";
    main.className = "project-card-main";
    main.addEventListener("click", () => void this.openDetail(p));

    const name = document.createElement("h3");
    name.className = "project-card-name";
    name.textContent = p.name;
    main.appendChild(name);

    // 2026-08-26 (Norm B6): Die Beschreibung begann oft mit demselben Namen,
    // der eine Zeile darueber schon als Ueberschrift steht.
    const kurz = vorschauOhneEcho(p.description ?? "", p.name ?? "");
    if (kurz) {
      const desc = document.createElement("p");
      desc.className = "project-card-desc";
      desc.textContent = kurz;
      main.appendChild(desc);
    }

    /*
     * Die Metazeile — wie viel steckt drin, und wann war zuletzt etwas los.
     * (22.09.2026)
     *
     * Martin: *„Bitte auch mit Zeitstempelangabe und vielleicht auch die
     * Anzeige der Anzahl der Dokumente und die aktualisierte Version."*
     *
     * ⚠️ Eine Zeile, nicht drei Abzeichen. Die Karte traegt schon Name und
     *    Beschreibung; drei weitere Kaestchen daneben machen die Uebersicht
     *    unuebersichtlicher, nicht klarer.
     *
     * ⚠️ Nichts wird erfunden: ein Projekt ohne Verknuepfungen sagt „noch
     *    nichts verknuepft" statt „0 Dokumente", und ohne Aenderung steht
     *    das Anlegedatum da — mit dem Wort, das dazugehoert.
     */
    const meta = document.createElement("p");
    meta.className = "project-card-meta";
    meta.textContent = projektMeta(p, formatRelativeTs);
    main.appendChild(meta);

    if (p.archivedAt) {
      const badge = document.createElement("span");
      badge.className = "project-card-badge";
      badge.textContent = "archiviert";
      main.appendChild(badge);
    }
    if (this.active?.id === p.id) {
      const badge = document.createElement("span");
      badge.className = "project-card-badge project-card-badge-active";
      badge.textContent = "aktiv";
      main.appendChild(badge);
    }

    card.appendChild(main);

    const actions = document.createElement("div");
    actions.className = "project-card-actions";

    // Pre.795 (#54): nur noch Zustands-Button + „⋯ Mehr"-Menü statt der
    // gestapelten Button-Reihe (Martins „in der Webansicht unschön
    // untereinander"). Menü-Inhalt zustandsabhängig — archivierte Projekte
    // haben kein Umbenennen/Archivieren.
    if (!p.archivedAt) {
      if (this.active?.id !== p.id) {
        actions.appendChild(
          mkActionBtn("Aktivieren", () => this.handleSetActive(p.id)),
        );
      } else {
        actions.appendChild(
          mkActionBtn("Deaktivieren", () => this.handleSetActive(null)),
        );
      }
    } else {
      actions.appendChild(
        mkActionBtn("Reaktivieren", () => this.handleUnarchive(p.id)),
      );
    }
    const more = mkActionBtn("", () => {
      const items = p.archivedAt
        ? [
            {
              label: "Löschen",
              hint: "Endgültig entfernen",
              danger: true,
              onSelect: () => this.handleDelete(p),
            },
          ]
        : [
            { label: "Umbenennen", onSelect: () => this.handleRename(p) },
            {
              label: "Archivieren",
              hint: "Aus der aktiven Liste nehmen",
              onSelect: () => void this.handleArchive(p.id),
            },
            {
              label: "Löschen",
              hint: "Endgültig entfernen",
              danger: true,
              onSelect: () => this.handleDelete(p),
            },
          ];
      void import("./action-menu.js").then(({ openActionMenu }) =>
        openActionMenu(p.name, items),
      );
    });
    more.setAttribute("aria-label", "Mehr Aktionen");
    more.appendChild(icon("more", { size: 18 }));
    actions.appendChild(more);

    card.appendChild(actions);
    return card;
  }

  // --- Detail-Sheet (E3.4) ----------------------------------------------

  // Pre.215: Komplett überarbeitetes Project-Detail-Modal mit Tab-Layout.
  // Header: Stats + Quick-Actions. Tabs: Dateien / Erinnerungen / Mehr.
  private async openDetail(p: ProjectDto): Promise<void> {
    // #143: Projekt-Detail ist eine echte Vollseiten-Ansicht (in die Seite
    // eingehaengt), kein schwebendes Modal — genau wie Human Design &
    // Essenz-Spiegel. Der Modifier --full schaltet von zentriertem Modal auf
    // Vollbild (CSS), die Tab-/Inhalts-Logik bleibt unveraendert.
    const overlay = document.createElement("div");
    overlay.className = "project-sheet-overlay project-sheet-overlay--full";
    overlay.addEventListener("click", (ev) => {
      if (ev.target === overlay) closeTopmost();
    });

    const sheet = document.createElement("div");
    sheet.className = "project-sheet project-sheet-v2 project-sheet-v2--full";
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-label", `Projekt: ${p.name}`);

    // Header
    const isAlltag = p.name === "Mein Alltag";
    const head = document.createElement("header");
    head.className = "project-sheet-v2-head";
    if (isAlltag) head.classList.add("project-sheet-v2-head--alltag");

    const isActive = this.active?.id === p.id;

    /*
     * ⚠️⚠️ 15.09.2026 — DER KOPF HATTE DREI ZEILEN UND KEINEN TITEL.
     *
     * Martin, mit Foto: *„Die Breitenabstaende passen nicht, das Farbprofil
     * passt nicht. Das gehoert viel zusammengeraeumter."*
     *
     * Im Browser nachgemessen (390 Punkte, Wald hell):
     *
     *     Zeile            linke Kante   was dort stand
     *     Pfeil + Titel            40    Titel display:none (0 Punkte breit)
     *     Meta-Zeile               40    „5 Dateien · 0 Erinnerungen · aktiv"
     *     Zwei Knoepfe             40    Deaktivieren, Bericht erstellen
     *     Reiterleiste             16
     *     Inhalt                   32
     *
     * Drei Befunde auf einmal:
     *
     * 1. **Der Titel war weg.** `buildPageHeader` setzt
     *    `page-head--ort-in-pille` (display:none auf .hd-title), wenn die
     *    Ortspille denselben Namen traegt — und auf der Detailseite eines
     *    AKTIVEN Projekts trifft das immer zu. Genau dafuer stand seit 2269
     *    die Gegenzeile im Code, aber hinter `if (isAlltag)`. Jedes andere
     *    Projekt stand ohne Ueberschrift da: ein Pfeil, dann Zaehlwerte.
     *    Die Pille ist der ORT, der Titel ist die SEITE — das gilt fuer alle.
     *
     * 2. **Drei eigene Zeilen, wo der geteilte Kopf drei Faecher hat.**
     *    `buildPageHeader` kennt Untertitel, EINE Aktion und ein ⋯-Menue.
     *    Der Projektkopf baute Meta und Knoepfe stattdessen daneben — mit
     *    eigenem Rand, eigener Hoehe, eigener Kante. Jetzt sitzt beides in
     *    den Faechern, die es schon gibt.
     *
     * 3. **Zwei sichtbare Kopf-Aktionen.** Die Norm im Kopf von sheet-ui.ts
     *    sagt: „GENAU EINE sichtbare Kopf-Aktion — mehr ins Menue." Beide
     *    stehen jetzt im ⋯: der Bericht laeuft Minuten und wird selten
     *    gebraucht, und das Aktivieren steht ohnehin in der Meta-Zeile
     *    („· aktiv"). Uebrig bleiben zwei Zeilen statt vier — derselbe Kopf
     *    wie auf allen anderen Seiten, was Martins Wunsch vom 12.07.2026
     *    war: *„einheitlich wie alle Module"*.
     *
     * ⚠️ Der sichtbare Knopf war auch technisch nicht haltbar: Aktion und
     *    Untertitel liegen im geteilten Raster BEIDE auf Zeile 2 — der Satz
     *    lief unter der Pille durch. Das ist getrennt behoben (style.css,
     *    `hd-head--action-and-lead`), weil es auch die Seite „Verbindung"
     *    trifft; hier braucht es die Aktion deshalb gar nicht mehr.
     */
    const menuItems: Array<{ label: string; onSelect: () => void }> = [];
    if (!p.archivedAt) {
      menuItems.push({
        // pre.907 (Martin): „Schließen" → „Deaktivieren" — das doppeldeutige
        // zweite „Schließen" (neben dem Eck-X) verwirrte. Logik unverändert.
        label: isActive ? "Deaktivieren" : "Aktivieren",
        onSelect: () => {
          void (async () => {
            await setActiveProject(isActive ? null : p.id);
            closeTopmost();
            await this.refresh();
          })();
        },
      });
      menuItems.push({
        label: "Bericht erstellen",
        onSelect: () => void starteBericht(),
      });
      if (isAlltag) {
        menuItems.push(
          { label: "Umbenennen", onSelect: () => this.handleRename(p) },
          { label: "Archivieren", onSelect: () => void this.handleArchive(p.id) },
        );
      }
    }

    // 2026-07-12 (Martin, "einheitlich wie alle Module"): Standard-Seitenkopf
    // (Pfeil links, Titel daneben, EINE Zeile) statt Eigenbau; der Farb-Punkt
    // fliesst inline vor dem Titel. reopenMenu:false = zurueck zur
    // Projektliste, Menue bleibt zu (Detail-Ebene, Muster von 2026-07-05).
    const pageHead = buildPageHeader(p.name, () => closeTopmost(), {
      reopenMenu: false,
      subtitle: "Lade …",
      menuItems: menuItems.length > 0 ? menuItems : undefined,
    });
    const dot = document.createElement("span");
    dot.className = "project-color-dot project-color-dot-lg";
    if (p.color) dot.style.background = p.color;
    pageHead.querySelector(".hd-title")?.prepend(dot);
    // 2269, seit heute fuer ALLE Projekte: die Pille ist der Ort, der Titel
    // ist die Seite. Ohne diese Zeile steht die Detailseite ohne Ueberschrift.
    pageHead.classList.remove("page-head--ort-in-pille");
    if (isAlltag) {
      pageHead.classList.add("page-head--alltag-titel");
      head.setAttribute("data-alltag-kopf", "1");
    }
    head.appendChild(pageHead);

    sheet.appendChild(head);

    // Tab-Bar
    const tabs = document.createElement("nav");
    tabs.className = "project-sheet-v2-tabs";
    tabs.setAttribute("role", "tablist");
    type TabId =
      | "overview"
      | "files"
      | "media"
      | "timeline"
      | "memory"
      | "more";
    let activeTab: TabId = "overview";
    const tabButtons = new Map<TabId, HTMLButtonElement>();

    const tabContent = document.createElement("div");
    tabContent.className = "project-sheet-v2-content";

    /*
     * ⚠️⚠️ 15.09.2026 — DIE LEISTE VERSCHWEIGT, DASS SIE WEITERGEHT.
     *
     * Gemessen auf 390 Punkten: Leiste 358 breit, Inhalt 681. Drei der sechs
     * Reiter lagen ausserhalb — kein Balken, keine Kante, kein Hinweis. Wer
     * nicht zufaellig wischt, hat „Erinnerungen" und „Einstellungen" nie
     * gesehen.
     *
     * Der Verlauf steht nur dort, wo wirklich noch etwas liegt; die Leiste
     * misst das selbst. 2 Punkte Spiel, weil Sub-Pixel sonst dauerhaft
     * „da ist noch was" melden, wenn man ganz am Ende steht.
     */
    const meldeRand = (): void => {
      const spiel = 2;
      const links = tabs.scrollLeft > spiel;
      const rechts =
        tabs.scrollLeft + tabs.clientWidth < tabs.scrollWidth - spiel;
      tabs.dataset.mehr = links && rechts
        ? "beides"
        : rechts
          ? "rechts"
          : links
            ? "links"
            : "nein";
    };
    tabs.addEventListener("scroll", gesteuert("wachen", meldeRand), { passive: true });

    const switchTab = (id: TabId): void => {
      activeTab = id;
      for (const [tid, btn] of tabButtons) {
        btn.dataset.active = String(tid === id);
      }
      // Der gewaehlte Reiter gehoert ins Bild — sonst tippt man auf etwas am
      // Rand und die Leiste laesst es dort stehen.
      tabButtons
        .get(id)
        ?.scrollIntoView({ inline: "nearest", block: "nearest" });
      meldeRand();
      void renderTab();
    };

    const tabDefs: ReadonlyArray<{ id: TabId; label: () => string }> = [
      { id: "overview", label: () => "Überblick" },
      { id: "files", label: () => `Dateien (${counts.files})` },
      {
        id: "media",
        label: () => (counts.media > 0 ? `Medien (${counts.media})` : "Medien"),
      },
      { id: "timeline", label: () => "Timeline" },
      { id: "memory", label: () => `Erinnerungen (${counts.memory})` },
      { id: "more", label: () => "Einstellungen" },
    ];
    const counts = { files: 0, memory: 0, media: 0 };
    for (const def of tabDefs) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "project-sheet-v2-tab";
      btn.dataset.tab = def.id;
      btn.dataset.active = String(def.id === activeTab);
      btn.setAttribute("role", "tab");
      btn.textContent = def.label();
      btn.addEventListener("click", () => switchTab(def.id));
      tabs.appendChild(btn);
      tabButtons.set(def.id, btn);
    }
    sheet.appendChild(tabs);
    sheet.appendChild(tabContent);

    overlay.appendChild(sheet);
    document.body.appendChild(overlay);
    // Erst im Baum hat die Leiste eine Breite — vorher waere jede Messung 0.
    meldeRand();
    pushView(overlay, { onClose: () => overlay.remove() });

    // Daten parallel laden
    const [filesPromise, detailPromise] = [
      listProjectFiles(p.id).catch(() => [] as ProjectFileDto[]),
      getProject(p.id).catch(() => ({ project: p, links: [] })),
    ];

    let cachedFiles: ProjectFileDto[] = [];
    let cachedLinks: ProjectLinkDto[] = [];

    const updateStats = (): void => {
      const memoryCount = cachedLinks.filter(
        (l) => l.targetType === "memory",
      ).length;
      counts.files = cachedFiles.length;
      counts.memory = memoryCount;
      const archived = p.archivedAt ? " · archiviert" : "";
      const active = !p.archivedAt && isActive ? " · aktiv" : "";
      const mediaCount = cachedLinks.filter(
        (l) => l.targetType === "upload",
      ).length;
      counts.media = mediaCount;
      const mediaPart = mediaCount > 0 ? ` · ${mediaCount} Medien` : "";
      /*
       * 07.09.2026 — „1 Dateien" stand da. Eine Zahl mit falschem Plural
       * liest sich wie ein Fehler im Zaehlen, auch wenn nur das Wort
       * daneben liegt.
       */
      const meta = `${zaehlwort(cachedFiles.length, "Datei", "Dateien")} · ${zaehlwort(memoryCount, "Erinnerung", "Erinnerungen")}${mediaPart}${active}${archived}`;
      // Die Meta-Zeile sitzt im Untertitel-Fach des geteilten Kopfes — seit
      // heute fuer ALLE Projekte, nicht nur fuer den Alltag.
      const sub = pageHead.querySelector(".hd-subtitle");
      if (sub) sub.textContent = meta;
      for (const def of tabDefs) {
        const btn = tabButtons.get(def.id);
        if (btn) btn.textContent = def.label();
      }
    };

    const renderTab = async (): Promise<void> => {
      while (tabContent.firstChild)
        tabContent.removeChild(tabContent.firstChild);
      if (activeTab === "overview") {
        await this.renderOverviewTab(tabContent, p);
      } else if (activeTab === "media") {
        this.renderMediaTab(tabContent, cachedLinks);
      } else if (activeTab === "timeline") {
        await this.renderTimelineTab(tabContent, p);
      } else if (activeTab === "files") {
        this.renderFilesTab(
          tabContent,
          p,
          cachedFiles,
          cachedLinks,
          async () => {
            cachedFiles = await listProjectFiles(p.id).catch(() => []);
            // Pre.804 (#72a): Medien-Links mit auffrischen (eine Closure).
            const fresh = await getProject(p.id).catch(() => null);
            if (fresh) cachedLinks = fresh.links;
            updateStats();
            if (activeTab === "files") void renderTab();
          },
        );
      } else if (activeTab === "memory") {
        this.renderMemoryTab(tabContent, p, cachedLinks, async () => {
          const fresh = await getProject(p.id).catch(() => ({
            project: p,
            links: [] as ProjectLinkDto[],
          }));
          cachedLinks = fresh.links;
          updateStats();
          if (activeTab === "memory") void renderTab();
        });
      } else {
        this.renderMoreTab(
          tabContent,
          p,
          dot,
          async () => {
            await this.refresh();
          },
          () => closeTopmost(),
        );
      }
    };

    void renderTab();

    Promise.all([filesPromise, detailPromise]).then(async ([files, detail]) => {
      cachedFiles = files;
      cachedLinks = detail.links;
      updateStats();
      void renderTab();
    });

    /*
     * Pre.804 (#73a): Bericht auf Knopfdruck — async (Opus-CLI), Ergebnis
     * erscheint als Notiz im Dateien-Tab + Viewer oeffnet sich.
     *
     * ⚠️ 15.09.2026: War ein zweiter sichtbarer Kopf-Knopf und steht jetzt im
     *    ⋯-Menue (Norm: EINE sichtbare Kopf-Aktion). Damit faellt der Knopf
     *    als Anzeigeflaeche weg — der Lauf braucht einen eigenen Ort, sonst
     *    passiert nach dem Tippen sichtbar nichts, und das ist genau die
     *    Sorte Warten, ueber die Martin sich zu Recht aergert. Die Zeile
     *    steht nur, solange der Bericht laeuft.
     */
    let berichtLaeuft = false;
    const laufZeile = document.createElement("p");
    laufZeile.className = "project-sheet-v2-lauf";
    laufZeile.hidden = true;
    laufZeile.setAttribute("role", "status");
    head.appendChild(laufZeile);

    const berichtEnde = (): void => {
      berichtLaeuft = false;
      laufZeile.hidden = true;
      laufZeile.textContent = "";
    };

    const starteBericht = async (): Promise<void> => {
      if (berichtLaeuft) return;
      berichtLaeuft = true;
      laufZeile.hidden = false;
      laufZeile.textContent = "Bericht läuft … das dauert ein paar Minuten.";
      try {
        await requestProjectReport(p.id);
      } catch (err) {
        berichtEnde();
        window.alert(
          `Bericht-Start fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
        );
        return;
      }
      let polls = 0;
      const t = window.setInterval(async () => {
        if (!overlay.isConnected || ++polls > 72) {
          window.clearInterval(t);
          berichtEnde();
          return;
        }
        const st = await getProjectReport(p.id).catch(() => null);
        if (!st) {
          window.clearInterval(t);
          berichtEnde();
          window.alert(
            "Bericht-Status verloren (Server neu gestartet?). Dateien-Tab prüfen.",
          );
          return;
        }
        if (st.status === "running") return;
        window.clearInterval(t);
        berichtEnde();
        if (st.status === "done" && st.fileId) {
          cachedFiles = await listProjectFiles(p.id).catch(() => cachedFiles);
          updateStats();
          if (activeTab === "files") void renderTab();
          void openProjectFileViewer(p.id, st.fileId, {});
        } else {
          window.alert(`Bericht fehlgeschlagen: ${st.error ?? "unbekannt"}`);
        }
      }, 5000);
    };
  }

  // --- Tab-Renderer (Pre.215) -------------------------------------------

  // Task 9: „Überblick" — das lebende, KI-erzeugte Projekt-Dokument
  // (XSS-sicher via renderMarkdown, KEIN innerHTML) + „Aktualisieren".
  private async renderOverviewTab(
    host: HTMLElement,
    p: ProjectDto,
  ): Promise<void> {
    host.replaceChildren();
    const bar = document.createElement("div");
    bar.className = "project-overview-bar";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "projects-btn projects-btn-primary";
    btn.textContent = "Aktualisieren";
    bar.appendChild(btn);
    // Pre.959: „Zuletzt aktualisiert"-Datum (zeitzonen-korrekt), sichtbar neben
    // dem Knopf; frischt sich beim Aktualisieren mit dem load() auf.
    const meta = document.createElement("span");
    meta.className = "project-overview-meta";
    bar.appendChild(meta);
    host.appendChild(bar);

    const body = document.createElement("div");
    body.className = "project-overview-body";
    host.appendChild(body);

    // Vorlese-Player (von app.ts injiziert). Wird über dem Text eingehängt,
    // nur wenn ein Dokument existiert; bei jedem load() neu aufgebaut, damit er
    // den aktuellen Überblickstext vertont (kein Stapeln mehrerer Player).
    let playerEl: HTMLElement | null = null;

    const load = async (): Promise<void> => {
      const data = await getProjectDocument(p.id).catch(() => null);
      // #182 Phase 2: Reiter könnte während des Fetch gewechselt worden sein →
      // body ist dann aus dem DOM gelöst; ein stale-Render würde werfen.
      if (!body.isConnected) return;
      body.replaceChildren();
      if (playerEl) {
        playerEl.remove();
        playerEl = null;
      }
      if (!data?.document) {
        meta.textContent = "";
        body.textContent =
          "Noch kein Überblick. Tippe oben auf Aktualisieren, dann wächst er aus dem Projekt-Material.";
        return;
      }
      const doc = data.document;
      const updatedLabel = formatDocDate(doc.updatedAt);
      meta.textContent = updatedLabel ? `Stand ${updatedLabel}` : "";
      const lang = formatDocDateLang(doc.updatedAt);
      if (lang) meta.title = `Zuletzt aktualisiert: ${lang}`;
      if (this.opts.buildPlayer) {
        // Pre.983: Überblick in Kapitel zerlegen → Vor/Zurück-Blättern im Player
        // (kurzer Überblick = 1 Abschnitt = keine Knöpfe; langer = Kapitel).
        const sections = splitDocChapters(doc.bodyMd);
        const player = this.opts.buildPlayer({
          endpoint: `/api/projects/${p.id}/narrate`,
          getBody: (voiceId) => ({
            text: sections[0]?.text ?? doc.bodyMd,
            voiceId,
          }),
          sectionable: true,
          // Positions-Resume pro Projekt, gebunden an die Dokument-Version:
          // ein neu generierter Überblick beginnt von vorn, sonst weiter wo zuletzt.
          resumeId: `proj:${p.id}`,
          resumeVersion: String(doc.version),
        });
        player.setSections(sections);
        playerEl = player.el;
        body.before(playerEl);
      }
      renderMarkdown(body, doc.bodyMd);
    };

    btn.addEventListener("click", () => {
      btn.disabled = true;
      btn.textContent = "Aktualisiere …";
      // Sichtbare Rückmeldung: die Generierung läuft über ein starkes Modell
      // und dauert typisch ~15-20 Sekunden — vorher wirkte es wie „passiert nichts".
      if (body.isConnected) {
        body.replaceChildren();
        body.textContent =
          "Erstelle den Überblick aus dem Projekt-Material … das kann etwa 20 Sekunden dauern.";
      }
      void regenerateProjectDocument(p.id)
        .then(() => load())
        .catch((err) => {
          // Fehler NICHT mehr stumm schlucken — dem User Rückmeldung geben.
          if (!body.isConnected) return;
          body.replaceChildren();
          body.textContent =
            "Aktualisieren hat nicht geklappt — bitte erneut versuchen. (" +
            (err instanceof Error ? err.message : "Fehler") +
            ")";
        })
        .finally(() => {
          btn.disabled = false;
          btn.textContent = "Aktualisieren";
        });
    });

    await load();
  }

  // #182 Phase 2: „Timeline" — datierte Meilensteine des Projekts.
  private async renderTimelineTab(
    host: HTMLElement,
    p: ProjectDto,
  ): Promise<void> {
    host.replaceChildren();
    const events = await getProjectTimeline(p.id).catch(() => []);
    if (!host.isConnected) return;
    if (!events.length) {
      host.textContent =
        "Noch keine Meilensteine. Sie entstehen automatisch, wenn der Überblick aus datiertem Material wächst.";
      return;
    }
    const list = document.createElement("div");
    list.className = "project-timeline";
    for (const ev of events) {
      const row = document.createElement("div");
      row.className = "project-timeline-event";
      const date = document.createElement("span");
      date.className = "project-timeline-date";
      date.textContent = ev.date
        ? new Date(ev.date).toLocaleDateString("de-DE", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : ev.dateText || "—";
      row.appendChild(date);
      const title = document.createElement("div");
      title.className = "project-timeline-title";
      title.textContent = ev.title;
      row.appendChild(title);
      if (ev.description) {
        const d = document.createElement("p");
        d.className = "project-timeline-desc";
        d.textContent = ev.description;
        row.appendChild(d);
      }
      list.appendChild(row);
    }
    host.appendChild(list);
  }

  // Task 9: „Medien" — eigenständige Galerie der verknüpften Mediathek-
  // Uploads. Wiederverwendung der Upload-/Thumbnail-Logik aus renderFilesTab
  // (gleiches project_links target_type="upload", gleicher m.url-Raw-Pfad).
  private renderMediaTab(host: HTMLElement, links: ProjectLinkDto[]): void {
    host.replaceChildren();
    type MediaLink = ProjectLinkDto & {
      preview?: string | null;
      url?: string;
      mime?: string;
    };
    const media = links.filter((l) => l.targetType === "upload") as MediaLink[];
    if (media.length === 0) {
      const empty = document.createElement("div");
      empty.className = "projects-empty";
      empty.textContent = "Noch keine Medien verknüpft.";
      host.appendChild(empty);
      return;
    }
    const grid = document.createElement("ul");
    grid.className = "project-media-grid";
    for (const m of media) {
      const li = document.createElement("li");
      li.className = "project-media-item";
      // 2026-07-01 (Martin): IMMER eine Karten-Kachel — Bild-Vorschau oder
      // Dokument-Icon + Dateityp, damit nichts als nackter Text schwebt.
      const thumb = document.createElement("button");
      thumb.type = "button";
      thumb.className = "project-media-thumb";
      thumb.addEventListener("click", () => {
        if (m.url) window.open(m.url, "_blank", "noopener,noreferrer");
      });
      if (m.url && (m.mime ?? "").startsWith("image/")) {
        const img = document.createElement("img");
        img.src = "/thumb?p=" + encodeURIComponent(m.url) + "&w=320";
        img.loading = "lazy";
        img.alt = m.preview ?? "";
        thumb.appendChild(img);
      } else {
        thumb.classList.add("is-doc");
        const glyph = document.createElement("span");
        glyph.className = "project-media-glyph";
        glyph.setAttribute("aria-hidden", "true");
        glyph.innerHTML =
          '<svg viewBox="0 0 24 24" width="26" height="26"><path fill="currentColor" d="M6 2h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm8 1.5V8h4.5L14 3.5z"/></svg>';
        thumb.appendChild(glyph);
        const mime = m.mime ?? "";
        const ext = mime.includes("pdf")
          ? "PDF"
          : mime.startsWith("audio/")
            ? "AUDIO"
            : mime.startsWith("video/")
              ? "VIDEO"
              : "DOK";
        const badge = document.createElement("span");
        badge.className = "project-media-ext";
        badge.textContent = ext;
        thumb.appendChild(badge);
      }
      li.appendChild(thumb);
      const label = document.createElement("button");
      label.type = "button";
      label.className = "project-media-title";
      label.textContent = m.preview ?? `${m.targetId.slice(0, 12)}…`;
      label.addEventListener("click", () => {
        if (m.url) window.open(m.url, "_blank", "noopener,noreferrer");
      });
      li.appendChild(label);
      grid.appendChild(li);
    }
    host.appendChild(grid);
  }

  private renderFilesTab(
    container: HTMLElement,
    p: ProjectDto,
    files: ProjectFileDto[],
    links: ProjectLinkDto[],
    refresh: () => Promise<void>,
  ): void {
    // Toolbar
    const toolbar = document.createElement("div");
    toolbar.className = "project-tab-toolbar";

    const newBtn = document.createElement("button");
    newBtn.type = "button";
    newBtn.className = "projects-btn projects-btn-primary";
    newBtn.textContent = "+ Neue Datei";
    newBtn.addEventListener("click", () =>
      this.openCreateFileSheet(p.id, () => void refresh()),
    );
    toolbar.appendChild(newBtn);

    const uploadBtn = document.createElement("button");
    uploadBtn.type = "button";
    uploadBtn.className = "projects-btn";
    uploadBtn.textContent = "↑ Hochladen";
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept =
      ".pdf,.png,.jpg,.jpeg,.gif,.webp,.mp3,.m4a,.wav,.webm,.ogg,.txt,.md";
    fileInput.hidden = true;
    uploadBtn.addEventListener("click", () => fileInput.click());
    fileInput.addEventListener("change", async () => {
      const f = fileInput.files?.[0];
      if (!f) return;
      const orig = uploadBtn.textContent;
      uploadBtn.disabled = true;
      uploadBtn.textContent = `↑ ${f.name.slice(0, 24)} …`;
      try {
        await uploadProjectFile(p.id, f);
        await refresh();
      } catch (err) {
        window.alert(
          `Upload fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
        );
      } finally {
        uploadBtn.disabled = false;
        uploadBtn.textContent = orig;
        fileInput.value = "";
      }
    });
    toolbar.appendChild(uploadBtn);
    toolbar.appendChild(fileInput);

    // 2026-07-05 (Martin): Aufraeumen — findet exakte Duplikate, zeigt eine
    // Vorschau, loescht erst nach Bestaetigung (Vorschau+Bestaetigen-Modus).
    const cleanBtn = document.createElement("button");
    cleanBtn.type = "button";
    cleanBtn.className = "projects-btn";
    cleanBtn.textContent = "\u2728 Aufr\u00e4umen";
    cleanBtn.addEventListener("click", async () => {
      const orig = cleanBtn.textContent;
      cleanBtn.disabled = true;
      cleanBtn.textContent = "Pr\u00fcfe \u2026";
      try {
        const plan = await getCleanupPlan(p.id);
        cleanBtn.disabled = false;
        cleanBtn.textContent = orig;
        const nameDups = plan.nameDuplicates ?? [];
        const upDups = plan.uploadDuplicates ?? [];
        const total =
          plan.duplicates.length + nameDups.length + upDups.length;
        if (total === 0) {
          await confirmSheet({
            title: "Alles sauber",
            message:
              "Ich habe keine doppelten Dateien gefunden \u2014 hier gibt es nichts zu entfernen.",
            confirmLabel: "Ok",
          });
          return;
        }
        const kb = (n: number): string => `${Math.round(n / 1024)} kB`;
        let message = "";
        if (plan.duplicates.length) {
          message +=
            `EXAKTE Doppel (Inhalt identisch) \u2014 je eine bleibt:\n` +
            plan.duplicates
              .map((d) => `\u2022 ${d.removeTitle} (${kb(d.sizeBytes)})`)
              .join("\n");
        }
        if (nameDups.length) {
          if (message) message += "\n\n";
          message +=
            `GLEICHER NAME, \u00e4ltere Version \u2014 die neueste bleibt:\n` +
            nameDups
              .map(
                (d) =>
                  `\u2022 ${d.removeTitle} (${kb(d.sizeBytes)}) \u2192 bleibt: ${d.keepTitle}`,
              )
              .join("\n");
        }
        if (upDups.length) {
          if (message) message += "\n\n";
          message +=
            `MEDIATHEK-Doppel (Inhalt identisch) \u2014 das Original bleibt:\n` +
            upDups
              .map((d) => `\u2022 ${d.removeTitle} (${kb(d.sizeBytes)})`)
              .join("\n");
        }
        message += `\n\nSpart ${kb(plan.bytesSaved)}. Das L\u00f6schen ist endg\u00fcltig.`;
        const ok = await confirmSheet({
          title: `${total} Datei(en) entsorgen?`,
          message,
          confirmLabel: "Aufr\u00e4umen",
          danger: true,
        });
        if (!ok) return;
        cleanBtn.disabled = true;
        cleanBtn.textContent = "R\u00e4ume auf \u2026";
        const ids = [
          ...plan.duplicates.map((d) => d.removeId),
          ...nameDups.map((d) => d.removeId),
        ];
        const upIds = upDups.map((d) => d.removeUploadId);
        const result = await applyCleanup(p.id, ids, upIds);
        await refresh();
        cleanBtn.disabled = false;
        cleanBtn.textContent = orig;
        window.dispatchEvent(
          new CustomEvent("voice-v8:toast", {
            detail: {
              message: `${result.deleted.length} doppelte Datei(en) entfernt.`,
              kind: "success",
            },
          }),
        );
      } catch (err) {
        cleanBtn.disabled = false;
        cleanBtn.textContent = orig;
        window.alert(
          `Aufr\u00e4umen fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    });
    toolbar.appendChild(cleanBtn);

    const searchInput = document.createElement("input");
    searchInput.type = "search";
    searchInput.placeholder = "Suche in Dateien …";
    searchInput.className = "project-tab-search";
    toolbar.appendChild(searchInput);

    container.appendChild(toolbar);

    const listEl = document.createElement("div");
    listEl.className = "project-tab-list";
    container.appendChild(listEl);

    const renderList = (filter: string): void => {
      while (listEl.firstChild) listEl.removeChild(listEl.firstChild);
      const q = filter.trim().toLowerCase();
      const filtered = q
        ? files.filter((f) => f.title.toLowerCase().includes(q))
        : files;
      if (filtered.length === 0) {
        const empty = document.createElement("div");
        empty.className = "projects-empty";
        empty.textContent = q
          ? "Keine Treffer."
          : "Noch keine Dateien. Lege eine an mit + Neue Datei.";
        listEl.appendChild(empty);
        return;
      }
      // Pre.358: Anzeige als 1 sortierte Liste (neueste zuerst) mit
      // Type-Badge, Größe, erstellt-am + bearbeitet-am. Vorher: nach Type
      // gruppiert, nur Size sichtbar. Martin wollte mehr Info pro File.
      const sorted = [...filtered].sort((a, b) =>
        (b.createdAt ?? "").localeCompare(a.createdAt ?? ""),
      );
      const byType = new Map<ProjectFileType, number>();
      for (const f of sorted) {
        byType.set(f.type, (byType.get(f.type) ?? 0) + 1);
      }
      const counts = Array.from(byType.entries())
        .map(([t, n]) => typeCountLabel(t, n))
        .join(" · ");
      const summary = document.createElement("div");
      summary.className = "project-files-summary";
      summary.textContent = counts;
      listEl.appendChild(summary);

      const ul = document.createElement("ul");
      ul.className = "project-files-list";
      for (const f of sorted) {
        const li = document.createElement("li");
        li.className = "project-file-item project-file-item--rich";
        const top = document.createElement("div");
        top.className = "project-file-row-top";
        const typeBadge = document.createElement("span");
        typeBadge.className = "project-file-type-badge";
        typeBadge.dataset.type = f.type;
        typeBadge.textContent = typeLabel(f.type);
        top.appendChild(typeBadge);
        const titleBtn = document.createElement("button");
        titleBtn.type = "button";
        titleBtn.className = "project-file-title";
        titleBtn.textContent = f.title;
        titleBtn.addEventListener(
          "click",
          // Pre.794 (#36): geteilter Viewer (lib/project-file-viewer) —
          // Projekte und Material nutzen dieselbe Detail-Ansicht.
          () =>
            void openProjectFileViewer(p.id, f.id, {
              onChange: () => void refresh(),
            }),
        );
        top.appendChild(titleBtn);
        li.appendChild(top);
        const meta = document.createElement("div");
        meta.className = "project-file-row-meta";
        const parts: string[] = [];
        parts.push(formatBytes(f.sizeBytes));
        // #196: absolutes Datum statt nur relativ — macht Dateien ähnlichen
        // Alters klar unterscheidbar.
        const absDate = (iso: string): string => {
          try {
            return new Date(iso).toLocaleDateString("de-AT", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            });
          } catch {
            return iso.slice(0, 10);
          }
        };
        parts.push(`erstellt ${absDate(f.createdAt)}`);
        if (f.updatedAt && f.updatedAt !== f.createdAt) {
          parts.push(`bearbeitet ${absDate(f.updatedAt)}`);
        }
        if (f.mime) parts.push(f.mime);
        meta.textContent = parts.join(" · ");
        meta.title = `Erstellt: ${f.createdAt}${f.updatedAt ? `\nBearbeitet: ${f.updatedAt}` : ""}`;
        li.appendChild(meta);
        ul.appendChild(li);
      }
      listEl.appendChild(ul);
    };

    searchInput.addEventListener("input", () => renderList(searchInput.value));
    renderList("");

    // Pre.804 (#72a): Verknüpfte Mediathek-Medien — lagen bisher unsichtbar
    // in project_links. Thumb + Titel (öffnet Medium) + Lösen.
    type MediaLink = ProjectLinkDto & {
      preview?: string | null;
      url?: string;
      mime?: string;
    };
    const media = links.filter((l) => l.targetType === "upload") as MediaLink[];
    if (media.length > 0) {
      const head = document.createElement("div");
      head.className = "project-files-summary";
      head.textContent = `Aus Mediathek (${media.length})`;
      container.appendChild(head);
      const mediaUl = document.createElement("ul");
      mediaUl.className = "project-media-grid";
      for (const m of media) {
        const li = document.createElement("li");
        li.className = "project-media-item";
        // 2026-07-01 (Martin): gleiche Karten-Optik wie der Medien-Tab.
        const thumb = document.createElement("button");
        thumb.type = "button";
        thumb.className = "project-media-thumb";
        thumb.addEventListener("click", () => {
          if (m.url) window.open(m.url, "_blank", "noopener,noreferrer");
        });
        if (m.url && (m.mime ?? "").startsWith("image/")) {
          const img = document.createElement("img");
          img.src = "/thumb?p=" + encodeURIComponent(m.url) + "&w=320";
          img.loading = "lazy";
          img.alt = m.preview ?? "";
          thumb.appendChild(img);
        } else {
          thumb.classList.add("is-doc");
          const glyph = document.createElement("span");
          glyph.className = "project-media-glyph";
          glyph.setAttribute("aria-hidden", "true");
          glyph.innerHTML =
            '<svg viewBox="0 0 24 24" width="26" height="26"><path fill="currentColor" d="M6 2h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm8 1.5V8h4.5L14 3.5z"/></svg>';
          thumb.appendChild(glyph);
          const mime = m.mime ?? "";
          const badge = document.createElement("span");
          badge.className = "project-media-ext";
          badge.textContent = mime.includes("pdf")
            ? "PDF"
            : mime.startsWith("audio/")
              ? "AUDIO"
              : "DOK";
          thumb.appendChild(badge);
        }
        li.appendChild(thumb);
        const label = document.createElement("button");
        label.type = "button";
        label.className = "project-media-title";
        label.textContent = m.preview ?? `${m.targetId.slice(0, 12)}…`;
        label.addEventListener("click", () => {
          if (m.url) window.open(m.url, "_blank", "noopener,noreferrer");
        });
        li.appendChild(label);
        const unlink = document.createElement("button");
        unlink.type = "button";
        unlink.className = "projects-btn";
        unlink.textContent = "Lösen";
        unlink.addEventListener("click", async () => {
          await unlinkFromProject(p.id, "upload", m.targetId).catch(() => {});
          await refresh();
        });
        li.appendChild(unlink);
        mediaUl.appendChild(li);
      }
      container.appendChild(mediaUl);
    }
  }

  private renderMemoryTab(
    container: HTMLElement,
    p: ProjectDto,
    links: ProjectLinkDto[],
    refresh: () => Promise<void>,
  ): void {
    const memoryLinks = links.filter((l) => l.targetType === "memory");

    const toolbar = document.createElement("div");
    toolbar.className = "project-tab-toolbar";
    const searchInput = document.createElement("input");
    searchInput.type = "search";
    searchInput.placeholder = "Suche in Erinnerungen …";
    searchInput.className = "project-tab-search";
    toolbar.appendChild(searchInput);
    container.appendChild(toolbar);

    const listEl = document.createElement("div");
    listEl.className = "project-tab-list";
    container.appendChild(listEl);

    // Pre.804 (#73b): Vorschläge — semantisch passende, noch nicht
    // verlinkte Erinnerungen (memory_search mit Projektname als Query).
    const sugLabel = document.createElement("div");
    sugLabel.className = "project-tab-more-label";
    sugLabel.textContent = "Vorschläge — passt zu diesem Projekt";
    const sugList = document.createElement("ul");
    sugList.className = "project-sheet-links-list";
    void listRelated(p.id)
      .then((items) => {
        if (items.length === 0 || !container.isConnected) return;
        container.appendChild(sugLabel);
        container.appendChild(sugList);
        for (const it of items) {
          const li = document.createElement("li");
          const text = document.createElement("span");
          text.className = "project-link-preview";
          text.textContent = it.preview;
          li.appendChild(text);
          const add = document.createElement("button");
          add.type = "button";
          add.className = "projects-btn";
          add.textContent = "Verknüpfen";
          add.addEventListener("click", async () => {
            add.disabled = true;
            try {
              await linkToProject(p.id, "memory", it.id);
              await refresh();
            } catch {
              add.disabled = false;
            }
          });
          li.appendChild(add);
          sugList.appendChild(li);
        }
      })
      .catch(() => {
        /* Vorschläge sind best-effort */
      });

    const renderList = (filter: string): void => {
      while (listEl.firstChild) listEl.removeChild(listEl.firstChild);
      const q = filter.trim().toLowerCase();
      const filtered = q
        ? memoryLinks.filter((l) => {
            const preview = (l as ProjectLinkDto & { preview?: string | null })
              .preview;
            return (preview ?? "").toLowerCase().includes(q);
          })
        : memoryLinks;
      if (filtered.length === 0) {
        const empty = document.createElement("p");
        empty.className = "project-sheet-links-empty";
        empty.textContent = q
          ? "Keine Treffer."
          : "Noch keine Erinnerungen verknüpft.";
        listEl.appendChild(empty);
        return;
      }
      const ul = document.createElement("ul");
      ul.className = "project-sheet-links-list";
      for (const link of filtered) {
        const li = document.createElement("li");
        li.title = link.targetId;
        const preview =
          (link as ProjectLinkDto & { preview?: string | null }).preview ??
          null;
        if (preview) {
          const text = document.createElement("span");
          text.className = "project-link-preview";
          text.textContent = preview;
          li.appendChild(text);
          const meta = document.createElement("span");
          meta.className = "project-link-meta";
          meta.textContent = formatRelativeTs(link.createdAt);
          li.appendChild(meta);
        } else {
          li.textContent = `${link.targetId.slice(0, 12)}…  ·  ${formatRelativeTs(
            link.createdAt,
          )}`;
        }
        ul.appendChild(li);
      }
      listEl.appendChild(ul);
    };

    searchInput.addEventListener("input", () => renderList(searchInput.value));
    renderList("");
  }

  private renderMoreTab(
    container: HTMLElement,
    p: ProjectDto,
    dot: HTMLElement,
    onChange: () => Promise<void>,
    closeOverlay: () => void,
  ): void {
    const wrap = document.createElement("div");
    wrap.className = "project-tab-more";

    // Beschreibung
    const descGroup = document.createElement("div");
    descGroup.className = "project-tab-more-group";
    const descLabel = document.createElement("label");
    descLabel.textContent = "Beschreibung";
    descLabel.className = "project-tab-more-label";
    descGroup.appendChild(descLabel);
    const descInput = document.createElement("textarea");
    descInput.className = "project-sheet-textarea";
    descInput.rows = 3;
    descInput.value = p.description ?? "";
    descInput.placeholder = "Worum geht es in diesem Projekt …";
    descGroup.appendChild(descInput);
    const descSave = document.createElement("button");
    descSave.type = "button";
    descSave.className = "projects-btn projects-btn-primary";
    descSave.textContent = "Beschreibung speichern";
    descSave.addEventListener("click", async () => {
      try {
        await updateProject(p.id, {
          description: descInput.value.trim() || null,
        });
        p.description = descInput.value.trim() || null;
        await onChange();
      } catch (err) {
        window.alert(
          `Fehler: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    });
    descGroup.appendChild(descSave);
    wrap.appendChild(descGroup);

    // Farbe
    const colorGroup = document.createElement("div");
    colorGroup.className = "project-tab-more-group";
    const colorLabel = document.createElement("label");
    colorLabel.textContent = "Farbe";
    colorLabel.className = "project-tab-more-label";
    colorGroup.appendChild(colorLabel);
    colorGroup.appendChild(
      renderColorPicker(p.color, async (next) => {
        try {
          await updateProject(p.id, { color: next });
          dot.style.background = next;
          p.color = next;
          await onChange();
        } catch (err) {
          window.alert(
            `Fehler: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      }),
    );
    wrap.appendChild(colorGroup);

    // Aktionen
    const actionGroup = document.createElement("div");
    actionGroup.className = "project-tab-more-group";
    const renameBtn = document.createElement("button");
    renameBtn.type = "button";
    renameBtn.className = "projects-btn";
    renameBtn.textContent = "Umbenennen …";
    renameBtn.addEventListener("click", async () => {
      const name = await promptSheet({
        title: "Projekt umbenennen",
        value: p.name,
        confirmLabel: "Umbenennen",
      });
      if (!name?.trim() || name === p.name) return;
      try {
        await updateProject(p.id, { name: name.trim() });
        closeOverlay();
        await onChange();
      } catch (err) {
        window.alert(
          `Fehler: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    });
    actionGroup.appendChild(renameBtn);

    // Endgültig-löschen-Knopf — in BEIDEN Zuständen verfügbar (direkt löschen,
    // ohne vorher zu archivieren). Geteilte Bestätigung via confirmProjectDelete.
    const buildDeleteBtn = (): HTMLButtonElement => {
      const delBtn = document.createElement("button");
      delBtn.type = "button";
      delBtn.className = "projects-btn project-action-danger";
      delBtn.textContent = "Endgültig löschen";
      delBtn.addEventListener("click", async () => {
        if (!(await confirmProjectDelete(p.name))) return;
        try {
          await deleteProject(p.id);
          closeOverlay();
          await onChange();
        } catch (err) {
          window.alert(
            `Fehler: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      });
      return delBtn;
    };

    if (p.archivedAt) {
      const unarchBtn = document.createElement("button");
      unarchBtn.type = "button";
      unarchBtn.className = "projects-btn";
      unarchBtn.textContent = "Reaktivieren";
      unarchBtn.addEventListener("click", async () => {
        try {
          await unarchiveProject(p.id);
          closeOverlay();
          await onChange();
        } catch (err) {
          window.alert(
            `Fehler: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      });
      actionGroup.appendChild(unarchBtn);
    } else {
      const archBtn = document.createElement("button");
      archBtn.type = "button";
      archBtn.className = "projects-btn";
      archBtn.textContent = "Archivieren";
      archBtn.addEventListener("click", async () => {
        const okArch = await confirmSheet({
          title: "Projekt archivieren?",
          message:
            "Es verschwindet aus der aktiven Liste und lässt sich jederzeit reaktivieren.",
          confirmLabel: "Archivieren",
        });
        if (!okArch) return;
        try {
          await archiveProject(p.id);
          closeOverlay();
          await onChange();
        } catch (err) {
          window.alert(
            `Fehler: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      });
      actionGroup.appendChild(archBtn);
    }
    actionGroup.appendChild(buildDeleteBtn());

    wrap.appendChild(actionGroup);
    container.appendChild(wrap);
  }

  // --- File-Viewer / -Editor (Sprint B) ----------------------------------

  private openCreateFileSheet(projectId: string, onCreated: () => void): void {
    const overlay = document.createElement("div");
    overlay.className = "project-sheet-overlay";
    overlay.addEventListener("click", (ev) => {
      if (ev.target === overlay) overlay.remove();
    });
    const sheet = document.createElement("div");
    sheet.className = "project-sheet project-file-sheet";
    sheet.setAttribute("role", "dialog");

    const close = buildCloseButton(() => overlay.remove());
    close.classList.add("close-btn--corner");
    sheet.appendChild(close);

    const head = document.createElement("header");
    head.className = "project-sheet-header page-head";
    const h = document.createElement("h2");
    h.className = "hd-title";
    h.textContent = "Neue Datei";
    head.appendChild(h);
    sheet.appendChild(head);

    const form = document.createElement("div");
    form.className = "project-file-form";

    const titleField = document.createElement("input");
    titleField.type = "text";
    titleField.placeholder = "Titel";
    titleField.className = "project-file-input";
    form.appendChild(titleField);

    const typeSelect = document.createElement("select");
    typeSelect.className = "project-file-input";
    const TYPES: ProjectFileType[] = [
      "note",
      "chapter",
      "character",
      "vocabulary",
      "transcript",
      "other",
    ];
    for (const t of TYPES) {
      const o = document.createElement("option");
      o.value = t;
      o.textContent = typeLabel(t);
      typeSelect.appendChild(o);
    }
    typeSelect.value = "note";
    form.appendChild(typeSelect);

    const contentField = document.createElement("textarea");
    contentField.className = "project-file-editor";
    contentField.placeholder = "# Titel\n\nMarkdown-Inhalt …";
    contentField.rows = 18;
    form.appendChild(contentField);

    const actions = document.createElement("div");
    actions.className = "project-file-actions";
    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "projects-btn projects-btn-primary";
    saveBtn.textContent = "Anlegen";
    saveBtn.addEventListener("click", async () => {
      const t = titleField.value.trim();
      const c = contentField.value;
      if (!t || !c) {
        window.alert("Titel und Inhalt sind Pflicht.");
        return;
      }
      try {
        await createProjectFile(projectId, {
          title: t,
          content: c,
          type: typeSelect.value as ProjectFileType,
        });
        overlay.remove();
        onCreated();
      } catch (err) {
        window.alert(
          `Anlegen fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    });
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "projects-btn";
    cancelBtn.textContent = "Abbrechen";
    cancelBtn.addEventListener("click", () => overlay.remove());
    actions.appendChild(saveBtn);
    actions.appendChild(cancelBtn);
    form.appendChild(actions);
    sheet.appendChild(form);

    overlay.appendChild(sheet);
    document.body.appendChild(overlay);
    setTimeout(() => titleField.focus(), 50);
  }

  // --- Actions ----------------------------------------------------------

  private async handleCreate(): Promise<void> {
    const initialColor =
      DEFAULT_COLORS[this.projects.length % DEFAULT_COLORS.length] ?? "#888";
    openCreateProjectSheet(
      initialColor,
      async ({ name, description, color }) => {
        try {
          await createProject({
            name,
            description: description || undefined,
            color,
          });
          await this.refresh();
        } catch (err) {
          window.alert(
            `Fehler: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      },
    );
  }

  private async handleRename(p: ProjectDto): Promise<void> {
    const name = await promptSheet({
      title: "Projekt umbenennen",
      value: p.name,
      confirmLabel: "Umbenennen",
    });
    if (!name?.trim() || name === p.name) return;
    try {
      await updateProject(p.id, { name: name.trim() });
      await this.refresh();
    } catch (err) {
      window.alert(
        `Fehler: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  private async handleSetActive(projectId: string | null): Promise<void> {
    try {
      await setActiveProject(projectId);
      await this.refresh();
      this.opts.onActiveChange?.(this.active);
    } catch (err) {
      window.alert(
        `Fehler: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  private async handleArchive(id: string): Promise<void> {
    const ok = await confirmSheet({
      title: "Projekt archivieren?",
      message:
        "Es verschwindet aus der aktiven Liste und lässt sich jederzeit reaktivieren.",
      confirmLabel: "Archivieren",
    });
    if (!ok) return;
    try {
      await archiveProject(id);
      await this.refresh();
      this.opts.onActiveChange?.(this.active);
    } catch (err) {
      window.alert(
        `Fehler: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  private async handleUnarchive(id: string): Promise<void> {
    try {
      await unarchiveProject(id);
      await this.refresh();
    } catch (err) {
      window.alert(
        `Fehler: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  private async handleDelete(p: ProjectDto): Promise<void> {
    if (!confirmProjectDelete(p.name)) return;
    try {
      await deleteProject(p.id);
      await this.refresh();
    } catch (err) {
      window.alert(
        `Fehler: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }
}

function mkActionBtn(label: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "project-action-btn";
  btn.textContent = label;
  btn.addEventListener("click", () => onClick());
  return btn;
}

/**
 * EINE Bestätigung fürs endgültige Löschen eines Projekts — geteilt von der
 * Listenkarte und dem Detail-Sheet. Der Text beschreibt EXAKT, was passiert:
 * das Projekt wird hart gelöscht, dabei räumt der Server (ON DELETE CASCADE)
 * Projekt-Dateien UND Verknüpfungen mit ab; ein aktives Projekt wird abgewählt.
 * Eigenständige Erinnerungen bleiben erhalten (nur die Verknüpfung verschwindet).
 */
function confirmProjectDelete(name: string): Promise<boolean> {
  return confirmSheet({
    title: `Projekt „${name}" endgültig löschen?`,
    message:
      "Alle Projekt-Dateien und Verknüpfungen werden mitgelöscht, ein " +
      "aktives Projekt wird abgewählt. Deine Erinnerungen bleiben erhalten. " +
      "Das lässt sich nicht rückgängig machen.",
    confirmLabel: "Endgültig löschen",
    danger: true,
  });
}

function openCreateProjectSheet(
  initialColor: string,
  onSubmit: (input: {
    name: string;
    description: string;
    color: string;
  }) => Promise<void> | void,
): void {
  const overlay = document.createElement("div");
  overlay.className = "project-sheet-overlay";
  overlay.addEventListener("click", (ev) => {
    if (ev.target === overlay) overlay.remove();
  });

  const sheet = document.createElement("div");
  sheet.className = "project-sheet project-sheet-create";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-modal", "true");

  const close = buildCloseButton(() => overlay.remove());
  close.classList.add("close-btn--corner");
  sheet.appendChild(close);

  const title = document.createElement("h2");
  title.className = "project-sheet-create-title";
  title.textContent = "Neues Projekt";
  sheet.appendChild(title);

  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.placeholder = "Name (z.B. Buch, Spanisch, Code)";
  nameInput.className = "project-sheet-input";
  nameInput.maxLength = 80;
  sheet.appendChild(nameInput);

  const descInput = document.createElement("textarea");
  descInput.placeholder = "Beschreibung (optional)";
  descInput.className = "project-sheet-textarea";
  descInput.rows = 2;
  descInput.maxLength = 500;
  sheet.appendChild(descInput);

  let chosenColor = initialColor;
  sheet.appendChild(
    renderColorPicker(initialColor, (c) => {
      chosenColor = c;
    }),
  );

  const actions = document.createElement("div");
  actions.className = "project-sheet-create-actions";
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "projects-btn";
  cancel.textContent = "Abbrechen";
  cancel.addEventListener("click", () => overlay.remove());
  const submit = document.createElement("button");
  submit.type = "button";
  submit.className = "projects-btn projects-btn-primary";
  submit.textContent = "Anlegen";
  submit.addEventListener("click", () => {
    const name = nameInput.value.trim();
    if (!name) {
      nameInput.focus();
      return;
    }
    overlay.remove();
    void onSubmit({
      name,
      description: descInput.value.trim(),
      color: chosenColor,
    });
  });
  actions.appendChild(cancel);
  actions.appendChild(submit);
  sheet.appendChild(actions);

  overlay.appendChild(sheet);
  document.body.appendChild(overlay);
  // Defer focus so iOS doesn't fight the inserted node
  requestAnimationFrame(() => nameInput.focus());
}

function renderColorPicker(
  current: string | null,
  onPick: (color: string) => Promise<void> | void,
): HTMLElement {
  const row = document.createElement("div");
  row.className = "project-color-picker";
  const label = document.createElement("span");
  label.className = "project-color-picker-label";
  label.textContent = "Farbe:";
  row.appendChild(label);
  for (const c of DEFAULT_COLORS) {
    const swatch = document.createElement("button");
    swatch.type = "button";
    swatch.className = "project-color-swatch";
    if (c === current) swatch.classList.add("is-active");
    swatch.style.background = c;
    swatch.setAttribute("aria-label", `Farbe ${c}`);
    swatch.addEventListener("click", () => {
      row.querySelectorAll(".project-color-swatch").forEach((el) => {
        el.classList.remove("is-active");
      });
      swatch.classList.add("is-active");
      void onPick(c);
    });
    row.appendChild(swatch);
  }
  return row;
}

/**
 * Wann war das? — in Worten. (22.09.2026 lesbar gemacht)
 *
 * ⚠️ Es gibt `relativeZeit()` in relative-time.js, und die wird auch
 *    benutzt (Glocke, Tickets, Klang). Sie bleibt hier trotzdem aussen vor,
 *    weil ihr das Ende fehlt: nach dreissig Tagen sagt sie „vor 150d", und
 *    das ist keine Auskunft mehr. Ein Projekt, an dem seit einem halben
 *    Jahr nichts passiert ist, soll sein DATUM zeigen.
 *
 * ⚠️ Vorher stand hier „3 d" und „14 h" — richtig gerechnet, aber in einer
 *    Sprache, die niemand spricht. Martin wollte eine Uebersicht, die
 *    professioneller wirkt; abgekuerzte Einheiten in einer sonst
 *    ausgeschriebenen Oberflaeche wirken das Gegenteil.
 */
function formatRelativeTs(iso: string): string {
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return iso;
  const deltaMin = Math.floor((Date.now() - ts) / 60_000);
  if (deltaMin < 1) return "gerade eben";
  if (deltaMin < 60) {
    return `vor ${deltaMin} ${deltaMin === 1 ? "Minute" : "Minuten"}`;
  }
  const hours = Math.floor(deltaMin / 60);
  if (hours < 24) return `vor ${hours} ${hours === 1 ? "Stunde" : "Stunden"}`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "gestern";
  if (days < 30) return `vor ${days} Tagen`;
  return `am ${new Date(iso).toLocaleDateString("de-DE")}`;
}

// --- Sprint B helpers ---------------------------------------------------

/**
 * Pre.795 (#54): Zahl VOR dem Label + Singular/Plural — „Kapitel: 1 ·
 * Sonstige: 1" las sich mit untergehendem Doppelpunkt als „Kapitel 1
 * Sonstiges" (Martins Befund).
 */
function typeCountLabel(type: ProjectFileType, n: number): string {
  const pair: Record<string, [string, string]> = {
    chapter: ["Kapitel", "Kapitel"],
    character: ["Person", "Personen"],
    note: ["Notiz", "Notizen"],
    recording: ["Aufnahme", "Aufnahmen"],
    transcript: ["Transcript", "Transcripte"],
    pdf: ["PDF", "PDFs"],
    image: ["Bild", "Bilder"],
    vocabulary: ["Vokabel-Liste", "Vokabel-Listen"],
    other: ["Sonstige Datei", "Sonstige Dateien"],
  };
  const [sg, pl] = pair[type] ?? pair.other;
  return `${n} ${n === 1 ? sg : pl}`;
}
